/**
 * Custom Brief dynamic section ids — one TOC entry per diligence question.
 */
import type { CustomBriefAnswer, CustomBriefQuestion } from '../types/siteRag';

export const CUSTOM_BRIEF_QUESTION_PREFIX = 'customBriefQ_';

export function customBriefQuestionSectionId(questionId: string): string {
  return `${CUSTOM_BRIEF_QUESTION_PREFIX}${questionId}`;
}

export function parseCustomBriefQuestionSectionId(sectionId: string): string | null {
  if (!sectionId.startsWith(CUSTOM_BRIEF_QUESTION_PREFIX)) return null;
  const qid = sectionId.slice(CUSTOM_BRIEF_QUESTION_PREFIX.length);
  return qid.length > 0 ? qid : null;
}

export function isCustomBriefQuestionSectionId(sectionId: string): boolean {
  return sectionId.startsWith(CUSTOM_BRIEF_QUESTION_PREFIX);
}

/** Build gen-time sectionOrder for a custom brief from question ids. */
export function buildCustomBriefSectionOrder(questionIds: readonly string[]): string[] {
  const qSections = questionIds
    .filter(id => typeof id === 'string' && id.trim().length > 0)
    .map(id => customBriefQuestionSectionId(id.trim()));
  return ['executiveSummary', 'projectContext', ...qSections, 'conclusion', 'appendix'];
}

export function buildCustomBriefSectionOrderFromQuestions(
  questions: readonly CustomBriefQuestion[],
): string[] {
  return buildCustomBriefSectionOrder(
    questions
      .filter(q => typeof q.text === 'string' && q.text.trim().length > 0)
      .map(q => q.id),
  );
}

/**
 * Resolve the 1-based Q index for a custom brief question.
 * Prefers persisted `questionNumber`, then answers-array position,
 * then position among `customBriefQ_*` ids in `sectionOrder`.
 */
export function resolveCustomBriefQuestionNumber(
  answers: readonly CustomBriefAnswer[] | undefined,
  questionId: string,
  sectionOrder?: readonly string[],
): number {
  const answer = answers?.find(a => a.questionId === questionId);
  if (
    typeof answer?.questionNumber === 'number'
    && Number.isFinite(answer.questionNumber)
    && answer.questionNumber >= 1
  ) {
    return Math.round(answer.questionNumber);
  }
  const idx = answers?.findIndex(a => a.questionId === questionId) ?? -1;
  if (idx >= 0) return idx + 1;
  if (sectionOrder?.length) {
    const qSections = sectionOrder.filter(isCustomBriefQuestionSectionId);
    const orderIdx = qSections.findIndex(id => parseCustomBriefQuestionSectionId(id) === questionId);
    if (orderIdx >= 0) return orderIdx + 1;
  }
  return 1;
}

/** Truncate question text (TOC / DOCX headings). */
export function customBriefQuestionLabel(question: string, maxLen = 60): string {
  const t = question.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 3)}…`;
}

/**
 * TOC label for a custom brief question.
 *
 * FIX-C — a TOC row must fit a single page line so the dotted leader + page
 * number stay inside the page box. A full-length question (e.g. "Q1. Is the
 * gas-fired BTM plan executable for Phase 1 (~250 MW, mid-2028 COD)?") ran off
 * the right page edge — clipped mid-word with NO leader or page number — because
 * the paged deck clips TOC overflow (`[data-section="toc"]{overflow:hidden}`).
 * Bound the label with an ellipsis (`maxLen` counts the `Qn. ` prefix) so it
 * always fits; the FULL question still drives the section body heading via
 * `customBriefQuestionHeading`. Short questions are unchanged.
 */
export function customBriefQuestionTocLabel(
  question: string,
  questionNumber?: number,
  maxLen = 64,
): string {
  const n = questionNumber ?? 1;
  const prefix = `Q${n}. `;
  const body = question.trim();
  const room = Math.max(8, maxLen - prefix.length);
  const truncated = body.length <= room ? body : `${body.slice(0, room - 1).trimEnd()}…`;
  return `${prefix}${truncated}`;
}

/** Full body heading — never truncated. */
export function customBriefQuestionHeading(question: string, questionNumber?: number): string {
  const n = questionNumber ?? 1;
  return `Q${n}. ${question.trim()}`;
}
